import { Application, extend } from "@pixi/react";
import { Container, Graphics, Sprite, Text } from "pixi.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DebugPanel } from "@app/hud/DebugPanel";
import { InspectorPanel } from "@app/hud/InspectorPanel";
import { Demo6Hud } from "@app/hud/Demo6Hud";
import { RebindingPanel } from "@app/hud/RebindingPanel";
import { InventoryPanel } from "@app/hud/InventoryPanel";
import { GlobalChatHud } from "@app/hud/GlobalChatHud";
import { SceneIntro } from "@app/hud/SceneIntro";
import { ActionBar, type ActionSlot } from "@app/hud/ActionBar";
import { Minimap } from "@app/hud/Minimap";
import { PlayerStatusBar } from "@app/hud/PlayerStatusBar";
import { EngineHost } from "@app/EngineHost";
import { useKeybindingsStore } from "@engine/state";
import { Inventory, ItemDB } from "@engine/gameplay";
import type { Scene } from "@app/scenes/SceneManager";
import type { EngineContextValue } from "@app/engine-context";

extend({ Container, Graphics, Sprite, Text });

const DEMOS = [
  { id: "demo1", label: "1 · Engine core (WASD + camera)" },
  { id: "demo2", label: "2 · Tiled map + collision" },
  { id: "demo3", label: "3 · ECS + 50 NPCs + chunks" },
  { id: "demo4", label: "4 · Spatial audio (Howler)" },
  { id: "demo5", label: "5 · Net interp/prediction (N=toggle)" },
  { id: "demo6", label: "6 · Polish (lighting + HUD + debug)" },
  { id: "demo7", label: "7 · Predict + reconcile (P/[/]/L)" },
  { id: "demo8", label: "8 · Stress: 500 animated NPCs" },
] as const;
type DemoId = (typeof DEMOS)[number]["id"];

const SCENE_INTROS: Record<DemoId, { title: string; lines: string[] }> = {
  demo1: {
    title: "Demo 1 — Engine core",
    lines: [
      "WASD / arrows to move · Space = camera shake · wheel = zoom",
      "Player is clamped to the blue boundary. Demo 2 swaps this for a real Tiled CollisionMap.",
    ],
  },
  demo2: {
    title: "Demo 2 — Tiled map + collision",
    lines: [
      "Same controls. Now there's a Tiled map: stone walls / water / fences / trees are SOLID.",
      "Walk through a tree base — its top stays drawn ABOVE you. Press F2 to highlight collision.",
    ],
  },
  demo3: {
    title: "Demo 3 — ECS + 50 NPCs + chunks",
    lines: [
      "50 NPCs wander; chunks far from you sleep (saving CPU). F3 visualizes chunks, F2 collision.",
      "Alt+click any NPC for the Inspector panel on the right.",
    ],
  },
  demo4: {
    title: "Demo 4 — Spatial audio (Howler)",
    lines: [
      "Yellow circles = audio emitters with full / fade / cutoff radii. Walk into them to hear.",
      "Green rectangles = ambient regions (cross-fading background tracks).",
    ],
  },
  demo5: {
    title: "Demo 5 — Network interpolation",
    lines: [
      "20 purple ghosts simulate remote players. They look smooth because of snapshot interpolation.",
      "Press N to TOGGLE interpolation off — see them stutter at the raw 10 Hz tick rate.",
    ],
  },
  demo6: {
    title: "Demo 6 — Polish (lighting + HUD)",
    lines: [
      "Animated sprites, nameplates, healthbars, deferred lighting (lightmap multiply).",
      "T = damage  ·  L = toggle lighting  ·  Alt+click = inspect  ·  F1..F4 = debug overlays",
      "Ambient presets: 1=night  2=moonlight  3=dusk  4=sunset  5=cave  6=arcane  7=storm  8=volcano  9=underwater  0=daylight",
    ],
  },
  demo7: {
    title: "Demo 7 — Predict + reconcile",
    lines: [
      "BLUE = you (predicted instantly). GREEN ring = where the SERVER thinks you are (with latency).",
      "P = toggle prediction off · [ / ] = ±50ms latency · L = cycle packet loss 0/5/15%.",
    ],
  },
  demo8: {
    title: "Demo 8 — Stress (500 animated NPCs)",
    lines: [
      "All systems on: animation, entity-vs-entity collision, chunks, lighting. F1 shows live FPS / ms / objects.",
    ],
  },
};

const SCENE_LOADERS: Record<DemoId, () => Promise<Scene>> = {
  demo1: () => import("@app/scenes/Demo1Scene").then((m) => new m.Demo1Scene()),
  demo2: () => import("@app/scenes/Demo2Scene").then((m) => new m.Demo2Scene()),
  demo3: () => import("@app/scenes/Demo3Scene").then((m) => new m.Demo3Scene()),
  demo4: () => import("@app/scenes/Demo4Scene").then((m) => new m.Demo4Scene()),
  demo5: () => import("@app/scenes/Demo5Scene").then((m) => new m.Demo5Scene()),
  demo6: () => import("@app/scenes/Demo6Scene").then((m) => new m.Demo6Scene()),
  demo7: () => import("@app/scenes/Demo7Scene").then((m) => new m.Demo7Scene()),
  demo8: () => import("@app/scenes/Demo8Scene").then((m) => new m.Demo8Scene()),
};

export function App() {
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const [activeDemo, setActiveDemo] = useState<DemoId>("demo1");
  const [engine, setEngine] = useState<EngineContextValue | null>(null);
  const [rebindOpen, setRebindOpen] = useState(false);
  const persistedBindings = useKeybindingsStore((s) => s.json);

  // Sample inventory + ItemDB for the showcase. A real game would build these
  // from server data on character load.
  const { sampleInventory, sampleItemDb } = useMemo(() => {
    const itemDb = new ItemDB()
      .add({ id: 1, name: "Bread",   rarity: "common",   stackable: true,  maxStack: 20 })
      .add({ id: 2, name: "Potion",  rarity: "uncommon", stackable: true,  maxStack: 10 })
      .add({ id: 3, name: "Iron Sword", rarity: "uncommon", stackable: false, maxStack: 1, equipSlot: "main_hand", stats: { atk: 6 } })
      .add({ id: 4, name: "Steel Helm", rarity: "rare",     stackable: false, maxStack: 1, equipSlot: "head",      stats: { def: 4 } })
      .add({ id: 5, name: "Mana Crystal", rarity: "epic",   stackable: true,  maxStack: 5 })
      .add({ id: 6, name: "Phoenix Feather", rarity: "legendary", stackable: false, maxStack: 1 });
    const inv = new Inventory(itemDb, 24);
    inv.add(1, 7);
    inv.add(2, 3);
    inv.add(3, 1);
    inv.add(4, 1);
    inv.add(5, 2);
    inv.add(6, 1);
    return { sampleInventory: inv, sampleItemDb: itemDb };
  }, []);

  // Sample action bar — wired in scenes that show the player.
  const sampleSlots: ActionSlot[] = useMemo(
    () => [
      { key: "1", hotkey: "1", name: "Slash",     iconChar: "⚔", color: "#fbbf24" },
      { key: "2", hotkey: "2", name: "Block",     iconChar: "🛡", color: "#94a3b8" },
      { key: "3", hotkey: "3", name: "Heal",      iconChar: "✚", color: "#4ade80", cooldownDurationS: 4 },
      { key: "4", hotkey: "4", name: "Fireball",  iconChar: "🔥", color: "#ef4444", cooldownDurationS: 6 },
      { key: "5", hotkey: "5", name: "Frostbolt", iconChar: "❄", color: "#60a5fa", cooldownDurationS: 6 },
      { key: "6", hotkey: "6", name: "Bow shot",  iconChar: "🏹", color: "#a78bfa" },
      { key: "7", hotkey: "Q", name: "Dash",      iconChar: "»",  color: "#22d3ee", cooldownDurationS: 3 },
      { key: "8", hotkey: "E", name: "Interact",  iconChar: "✦",  color: "#facc15" },
    ],
    [],
  );
  // Map state: which scenes show the MMO HUD, and minimap world dims.
  const mmoHudConfig: Partial<Record<DemoId, { mapW: number; mapH: number; tileSize: number }>> = useMemo(
    () => ({
      demo3: { mapW: 24, mapH: 16, tileSize: 32 },
      demo6: { mapW: 24, mapH: 16, tileSize: 32 },
      demo8: { mapW: 96, mapH: 96, tileSize: 32 }, // wide stress field
    }),
    [],
  );
  const showsMmoHud = activeDemo in mmoHudConfig;
  const mmoCfg = mmoHudConfig[activeDemo];

  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onEngineReady = useCallback(
    async (ctx: EngineContextValue) => {
      setEngine(ctx);
      ctx.game.actions.loadJSON(persistedBindings);
      const scene = await SCENE_LOADERS.demo1();
      await ctx.scenes.go(scene);
    },
    [persistedBindings],
  );

  useEffect(() => {
    if (!engine) return;
    let cancelled = false;
    void (async () => {
      const scene = await SCENE_LOADERS[activeDemo]();
      if (cancelled) return;
      await engine.scenes.go(scene);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeDemo, engine]);

  return (
    <>
      <Application
        width={size.width}
        height={size.height}
        background={0x0b0d12}
        antialias
        autoDensity
        resolution={window.devicePixelRatio}
        preference="webgpu"
      >
        <EngineHost onReady={onEngineReady}>
          <pixiContainer />
        </EngineHost>
      </Application>
      <DebugPanel sceneName={activeDemo} game={engine?.game} />
      <InspectorPanel game={engine?.game} />
      <InventoryPanel game={engine?.game} inventory={sampleInventory} itemDb={sampleItemDb} />
      <GlobalChatHud />
      <SceneIntro active={activeDemo} intros={SCENE_INTROS} />
      {showsMmoHud && (
        <>
          <ActionBar game={engine?.game} slots={sampleSlots} />
          <PlayerStatusBar
            name="Hero"
            level={12}
            hp={184}
            hpMax={250}
            mp={88}
            mpMax={120}
            xp={4250}
            xpMax={6000}
          />
          <Minimap
            game={engine?.game}
            worldWidth={mmoCfg?.mapW ?? 24}
            worldHeight={mmoCfg?.mapH ?? 16}
            tileSize={mmoCfg?.tileSize ?? 32}
          />
        </>
      )}
      {activeDemo === "demo6" && <Demo6Hud game={engine?.game} />}
      <RebindingPanel game={engine?.game} open={rebindOpen} onClose={() => setRebindOpen(false)} />
      <DemoSwitcher active={activeDemo} onSelect={setActiveDemo} onRebind={() => setRebindOpen(true)} />
    </>
  );
}

function DemoSwitcher({
  active,
  onSelect,
  onRebind,
}: {
  active: DemoId;
  onSelect: (id: DemoId) => void;
  onRebind: () => void;
}) {
  return (
    <div
      data-testid="demo-switcher"
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        right: 8,
        display: "flex",
        gap: 6,
        zIndex: 1000,
        flexWrap: "wrap",
        maxHeight: "40vh",
        overflowY: "auto",
      }}
    >
      {DEMOS.map((d) => (
        <button
          key={d.id}
          type="button"
          onClick={() => onSelect(d.id)}
          style={{
            padding: "6px 10px",
            fontSize: 12,
            background: d.id === active ? "#2563eb" : "rgba(0,0,0,0.55)",
            color: "#e6e8ee",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 6,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {d.label}
        </button>
      ))}
      <button
        type="button"
        onClick={onRebind}
        title="Open keybinding rebind modal"
        style={{
          padding: "6px 10px",
          fontSize: 12,
          background: "rgba(60, 30, 110, 0.85)",
          color: "#e6e8ee",
          border: "1px solid rgba(168,85,247,0.6)",
          borderRadius: 6,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        ⌨ Rebind keys
      </button>
    </div>
  );
}
